/**
 * Ek yukleme - **GONDERIRKEN DEGIL, ALIRKEN**.
 *
 * ## Neden alirken
 *
 * Kullanici dosyayi secer/yapistirir/surukler; yukleme HEMEN basliyor ve
 * belge cikarimi (PyMuPDF / LibreOffice) sunucuda o anda kosuyor. Kazanc
 * somut: cikarim gecikmesi KRITIK YOLDAN cikiyor. Kullanici yazmaya devam
 * ederken "belge okunuyor…" goruyor - donmus bir gonder butonuna bakmak
 * yerine.
 *
 * ## Neden `XMLHttpRequest`, `fetch` DEGIL
 *
 * `fetch`in **upload progress olayi YOK**. Streaming request body HTTP/2 +
 * `duplex: 'half'` istiyor ve burada ikisi de yok (uvicorn HTTP/1.1). Yuzde
 * gostermek icin `xhr.upload.onprogress` tek yol. Bu, `fetch`e gecmek icin
 * cazip ama gecince ilerleme cubugu SESSIZCE olur.
 *
 * ## Istekte HICBIR YERDE base64 yok
 *
 * Onceki halde resimler mesaj icinde base64 data-URL olarak tasiniyordu ve
 * 3 resimli bir konusma HER TURDA ~4 MB yeniden yukleniyordu. Artik yalnizca
 * `attachment_ids` gidiyor. Migration'in en buyuk tek payload kazanci bu.
 */

import { i18next } from '../i18n'

export type ChipState = 'queued' | 'uploading' | 'processing' | 'ready' | 'error'

export interface UploadedAttachment {
  id: string
  kind: 'image' | 'video' | 'audio' | 'document' | 'text'
  filename: string
  mime: string
  byte_size: number
  width: number | null
  height: number | null
  extract_status: string
  page_image_count: number
  url: string
  /** Video: ilk karenin adresi (kucuk resim). Cozumleme bitmeden `null`. */
  thumb_url?: string | null
  /** Sesli mesaj: kaydin suresi (saniye) ve transkripti. */
  duration_seconds?: number | null
  transcript?: string | null
  text_chars?: number
  text_preview?: string
}

/** Composer'daki bir ek chip'i. */
export interface Chip {
  /** Istemci tarafi kimlik - sunucu id'si daha yok. */
  localId: string
  file: File
  state: ChipState
  /** 0-100; yalnizca `uploading` sirasinda anlamli. */
  progress: number
  /** Yukleme bitince sunucudan gelen kayit. */
  uploaded?: UploadedAttachment
  error?: string
  /** Iptal icin. */
  abort?: () => void
}

export interface UploadCallbacks {
  onProgress(percent: number): void
  /** Baytlar gitti, sunucu isliyor (cikarim). */
  onProcessing(): void
}

/**
 * Tek dosya yukler.
 *
 * `csrfToken` ACIKCA veriliyor: `api()` sarmalayicisi `fetch` uzerine kurulu
 * ve burada `XMLHttpRequest` kullaniyoruz, yani otomatik CSRF eklemesi
 * devreye girmiyor. Unutulmasi CSRF middleware'inde 403 demek.
 */
export function uploadFile(
  file: File,
  csrfToken: string | null,
  callbacks: UploadCallbacks,
): { promise: Promise<UploadedAttachment>; abort(): void } {
  const xhr = new XMLHttpRequest()
  const form = new FormData()
  form.append('file', file, file.name)

  const promise = new Promise<UploadedAttachment>((resolve, reject) => {
    xhr.open('POST', '/api/attachments')
    xhr.withCredentials = true
    if (csrfToken) xhr.setRequestHeader('X-CSRF-Token', csrfToken)

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return
      const percent = Math.round((event.loaded / event.total) * 100)
      callbacks.onProgress(percent)
      // Baytlar gitti: bundan sonrasi SUNUCU tarafi cikarim ve saniyeler
      // surebiliyor. Yuzdeyi %100'de dondurup birakmak, kullaniciya
      // uygulamanin takildigini dusundururdu.
      if (percent >= 100) callbacks.onProcessing()
    }

    xhr.onload = () => {
      if (xhr.status === 201) {
        try {
          resolve(JSON.parse(xhr.responseText) as UploadedAttachment)
        } catch {
          reject(new Error(i18next.t('attach.error.badResponse')))
        }
        return
      }
      reject(new Error(readError(xhr)))
    }

    xhr.onerror = () => reject(new Error(i18next.t('attach.error.network')))
    xhr.onabort = () => reject(new AbortError())
    xhr.send(form)
  })

  return { promise, abort: () => xhr.abort() }
}

/** Kullanici iptali - hata olarak GOSTERILMEMELI. */
export class AbortError extends Error {}

/**
 * Sunucu hata mesajini cikarir.
 *
 * Sunucu Turkce ve kaba mesaj donduruyor (413 "Dosya cok buyuk", 422
 * "Desteklenmeyen tur"); ham exception metni yok. Burada yalnizca okuyoruz.
 */
function readError(xhr: XMLHttpRequest): string {
  try {
    const body = JSON.parse(xhr.responseText)
    // Sinir panelden degisebiliyor: sunucu yururlukteki siniri gonderiyor,
    // mesaj kullanicinin dilinde kuruluyor ("en fazla 25 MB").
    const limit = body?.error?.details?.limit_bytes
    if (body?.error?.code === 'file_too_large' && typeof limit === 'number' && limit > 0) {
      return i18next.t('attach.error.tooLargeLimit', { size: `\u2068${formatBytes(limit)}\u2069` })
    }
    if (typeof body?.detail === 'string') return body.detail
  } catch {
    // Govde JSON degil.
  }
  if (xhr.status === 413) return i18next.t('attach.error.tooLarge')
  if (xhr.status === 422) return i18next.t('attach.error.unsupported')
  if (xhr.status === 401 || xhr.status === 403) return i18next.t('attach.error.unauthorized')
  return i18next.t('attach.error.failedStatus', { status: xhr.status })
}

// -------------------------------------------------------------- yardimcilar

/**
 * Kabul edilen uzantilar.
 *
 * Sunucu da ayni listeyi zorluyor (`services/attachments.py`); buradaki
 * kopya yalnizca dosya secicisini filtrelemek ve kullaniciyi
 * yukle-bekle-reddedil turundan kurtarmak icin. Guvenlik siniri DEGIL.
 */
export const ACCEPT =
  // Video: kareler + konusma metni olarak cozumleniyor (sunucu tarafi).
  '.mp4,.mov,.m4v,.webm,.mkv,.avi,' +
  // Ses: sesli mesaj - yuklemede metne cevriliyor, mesajda oynatici olarak.
  '.mp3,.m4a,.wav,.ogg,.oga,.opus,.flac,' +
  '.png,.jpg,.jpeg,.webp,.gif,.bmp,.pdf,.docx,.xlsx,.pptx,.odt,.ods,.rtf,' +
  '.txt,.md,.markdown,.json,.yaml,.yml,.csv,.tsv,.log,.sql,.py,.js,.ts,.tsx,' +
  '.jsx,.java,.c,.cpp,.h,.cs,.go,.rs,.rb,.php,.sh,.ps1,.html,.css,.xml,.ini,.toml'

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** Chip etiketinde gosterilecek durum metni. */
export function chipStatusText(chip: Chip): string {
  switch (chip.state) {
    case 'queued':
      return i18next.t('attach.state.queued')
    case 'uploading':
      // Turkce % isaretini ONE aliyor (%40), Ingilizce SONA (40%).
      return i18next.t('attach.state.percent', { percent: chip.progress })
    case 'processing':
      // Belge cikarimi saniyeler surebiliyor ve kullanici ne beklendigini
      // bilmeli.
      return i18next.t('attach.state.processing')
    case 'error':
      return chip.error ?? i18next.t('attach.state.error')
    case 'ready':
      return formatBytes(chip.file.size)
  }
}

/** Gonderilebilir mi? TUM chip'ler hazir olmadan gonderim YOK. */
export function allReady(chips: Chip[]): boolean {
  return chips.every((chip) => chip.state === 'ready')
}

/** Sunucuya gonderilecek id'ler. */
export function readyIds(chips: Chip[]): string[] {
  return chips.flatMap((chip) => (chip.uploaded ? [chip.uploaded.id] : []))
}
